import GuideLayout from '@/pages/shared/GuideLayout';

export default function GuideCriticalPath() {
  return (
    <GuideLayout
      title="クリティカルパスとは？計算方法とプロジェクト管理での活用法｜”おもいむすび”"
      description="クリティカルパス法（CPM）の基本概念とプロジェクト管理における活用法を解説。クリティカルパスの見つけ方、遅延がプロジェクト全体に与える影響、スケジュール短縮のポイント。"
      keywords="プロジェクト管理 クリティカルパス,クリティカルパスとは,クリティカルパス 計算,CPM,プロジェクト 最短経路"
      canonical="https://omoimusubi.asia/guides/critical-path/"
      h1="クリティカルパスとは？プロジェクト管理での活用法"
      subtitle="プロジェクトの最短完了日数を決めるクリティカルパスの考え方と、スケジュール管理への活用法を解説します。"
      relatedFeatures={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
      ]}
      relatedGuides={[
        { label: 'プロジェクト依存関係', path: '/guides/project-dependency/' },
        { label: 'ガントチャートとは', path: '/guides/gantt-chart/' },
        { label: 'プロジェクトスケジュール管理', path: '/guides/project-schedule-management/' },
        { label: 'プロジェクト進捗管理', path: '/guides/project-progress-management/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20scene%20a%20Japanese%20project%20manager%20a%20woman%20in%20her%2030s%20standing%20in%20front%20of%20a%20large%20monitor%20displaying%20a%20project%20network%20diagram%20with%20highlighted%20critical%20path%20in%20bright%20color%20showing%20the%20longest%20chain%20of%20dependent%20tasks%20she%20is%20pointing%20at%20the%20critical%20path%20with%20a%20determined%20focused%20expression%20natural%20daylight%20through%20large%20windows%20indoor%20plants%20refreshing%20professional%20Japanese%20corporate%20atmosphere&width=1200&height=600&seq=guide-critical-path-01&orientation=landscape"
          alt="クリティカルパスとは - ネットワーク図のクリティカルパスを指し示す日本人プロジェクトマネージャー"
          className="w-full h-auto"
        />
      </div>
      <section>
        <h2>クリティカルパスとは</h2>
        <p>クリティカルパスとは、プロジェクトの開始から完了までに通るタスクの経路の中で、最も所要時間が長くなる経路のことです。クリティカルパス上のタスクが1日でも遅れると、プロジェクト全体の完了日が1日遅れることになります。</p>
        <p>逆に、クリティカルパス上にないタスクには「フロート（余裕日数）」があり、多少遅れてもプロジェクト全体の完了日には影響しません。</p>
      </section>

      <section>
        <h2>クリティカルパスの計算方法</h2>
        <p>クリティカルパスは以下の手順で求めます。</p>
        <ol>
          <li><strong>全タスクと依存関係を洗い出す</strong>：WBSと依存関係をもとに、プロジェクトのネットワーク図を作成します。</li>
          <li><strong>最早開始日（ES）と最早終了日（EF）を計算する</strong>：プロジェクト開始から順方向に、各タスクの最も早い開始日と終了日を計算します。</li>
          <li><strong>最遅開始日（LS）と最遅終了日（LF）を計算する</strong>：プロジェクト完了日から逆方向に、各タスクの最も遅い開始日と終了日を計算します。</li>
          <li><strong>フロートを計算する</strong>：LS - ES = フロート（余裕日数）。フロートが0のタスクをつなげた経路がクリティカルパスです。</li>
        </ol>
        <p>手計算では複雑ですが、”おもいむすび”のAIガントチャートでは、依存関係を設定するだけでクリティカルパスが自動計算され、強調表示されます。</p>
      </section>

      <section>
        <h2>クリティカルパス管理のポイント</h2>
        <ul>
          <li><strong>クリティカルパス上のタスクを重点管理する</strong>：これらのタスクの遅延はプロジェクト全体に直結するため、優先的に進捗確認とリソース配分を行います。</li>
          <li><strong>クリティカルパスは変化する</strong>：プロジェクトの進行に伴い、クリティカルパスが別の経路に移ることがあります。定期的な見直しが必要です。</li>
          <li><strong>バッファ管理を適切に</strong>：クリティカルパス上のタスクに過剰なバッファを持たせると、見かけ上のクリティカルパスが変わってしまいます。</li>
          <li><strong>クラッシングとファストトラッキング</strong>：クリティカルパスを短縮する手法として、リソース追加（クラッシング）やタスクの並行実施（ファストトラッキング）があります。</li>
        </ul>
      </section>

      <section>
        <h2>クリティカルパスを意識しない管理のリスク</h2>
        <p>クリティカルパスを意識せずにすべてのタスクを同じ優先度で管理すると、以下のような問題が発生します。</p>
        <ul>
          <li>フロートのあるタスク（多少遅れても全体に影響しないタスク）の遅延に過剰に反応してしまう</li>
          <li>本当に重要なタスク（クリティカルパス上）の遅延に気づくのが遅れる</li>
          <li>リソース配分の優先順位を誤り、プロジェクト全体が遅延する</li>
        </ul>
      </section>

      <section>
        <h2>AIによるクリティカルパス自動計算</h2>
        <p>”おもいむすび”では、AIがタスクの依存関係と所要期間をもとにクリティカルパスを自動計算します。ガントチャート上でクリティカルパスが強調表示され、どのタスクの遅延がプロジェクト全体に影響するかが一目で分かります。依存関係が変更された場合も、AIが即座にクリティカルパスを再計算し、最新の状態を反映します。</p>
      </section>
    </GuideLayout>
  );
}